import { useEffect, useMemo, useState } from "react";

/**
 * Reusable A-Z / Z-A / Default sort hook for any list of objects.
 *
 *   const { sorted, mode, label, cycle } = useNameSort(items, "users");
 *   …
 *   <button onClick={cycle}>{label}</button>
 *   {sorted.map(...)}
 *
 * @param items         Array of objects to sort.
 * @param storageKey    Sub-key for localStorage (so different lists keep
 *                      independent preferences). Final key is
 *                      "xyz_sort_<storageKey>".
 * @param nameOf        Function returning the string used for comparison.
 *                      Defaults to `it.name || it.display_name || it.title || ""`.
 * @param defaultMode   "name-asc" (default) | "name-desc" | "default".
 *                      "default" preserves the API/input order.
 */
export function useNameSort(items, storageKey, nameOf, defaultMode = "name-asc") {
  const fullKey = `xyz_sort_${storageKey}`;
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(fullKey) || defaultMode;
    } catch {
      return defaultMode;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(fullKey, mode);
    } catch {
      // ignore quota / private-mode errors
    }
  }, [fullKey, mode]);

  const getName = nameOf || ((it) =>
    String(it?.name || it?.display_name || it?.title || "").toUpperCase()
  );

  const sorted = useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];
    if (mode === "name-asc" || mode === "name-desc") {
      list.sort((a, b) => {
        const an = getName(a);
        const bn = getName(b);
        // Numeric-aware compare so "AGENCY 10" follows "AGENCY 9", not 1.
        const cmp = an.localeCompare(bn, "en", {
          numeric: true,
          sensitivity: "base",
        });
        return mode === "name-asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [items, mode]);

  const label =
    mode === "name-asc"
      ? "A → Z"
      : mode === "name-desc"
      ? "Z → A"
      : "Default";

  const cycle = () =>
    setMode((m) =>
      m === "name-asc" ? "name-desc" : m === "name-desc" ? "default" : "name-asc"
    );

  return { sorted, mode, label, cycle, setMode };
}
